/**
 * UserMenu — menu do avatar no cabeçalho.
 *
 * O "Sair" vivia solto na barra de topo, com contorno vermelho, ao lado do
 * avatar: era o elemento mais chamativo do cabeçalho sendo a acção menos
 * usada. Passou para dentro deste menu, no fim e separado do resto.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMinhasEmpresas } from '../hooks/useMinhasEmpresas';
import { useModoRecrutamento } from '../hooks/useModoRecrutamento';
import { filtrarNavegacao } from '../utils/modoRecrutamento';
import Icon from './Icon.js';
import { ROLE_LABELS } from '../constants';

const UserMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);
  const ancora = useRef(null);
  const botao = useRef(null);

  // Fecha ao clicar fora ou com Escape. Sem isto o menu ficava aberto por
  // cima do conteúdo e no telemóvel não havia forma óbvia de o fechar.
  useEffect(() => {
    if (!aberto) return undefined;
    const foraDoMenu = (e) => { if (!ancora.current?.contains(e.target)) setAberto(false); };
    const escape = (e) => {
      if (e.key === 'Escape') { setAberto(false); botao.current?.focus(); }
    };
    document.addEventListener('mousedown', foraDoMenu);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', foraDoMenu);
      document.removeEventListener('keydown', escape);
    };
  }, [aberto]);

  const sair = async () => {
    setAberto(false);
    await logout();
    navigate('/login', { replace: true });
  };

  const base = user?.role === 'worker' ? '/worker' : '/dashboard';
  const inicial = (user?.full_name || '?').trim().charAt(0).toUpperCase();
  // "Para Empresas" saiu daqui. Quem só quer ver a Dozy não deve encontrar
  // o registo da empresa dentro da conta de cliente. Quem já pertence a uma
  // continua a ter o atalho para a área.
  const { temEmpresa } = useMinhasEmpresas();
  const { activo: recrutamento } = useModoRecrutamento();

  const itens = user?.role === 'worker'
    ? [
        { para: '/worker',            icone: 'user',     texto: 'O meu painel' },
        { para: '/worker-profile/me', icone: 'briefcase', texto: 'O meu perfil' },
        { para: '/notifications',     icone: 'bell',     texto: 'Notificações' },
      ]
    : filtrarNavegacao([
        { to: `${base}?s=profile`,  icone: 'user',     texto: 'O meu perfil' },
        { to: '/obras',             icone: 'briefcase', texto: 'Obras & Projetos' },
        ...(temEmpresa ? [{ to: '/empresa', icone: 'bag', texto: 'Área da empresa' }] : []),
        { to: '/appointments',      icone: 'calendar', texto: 'Agendamentos' },
        { to: '/disputes',          icone: 'scale',    texto: 'Mediações' },
        { to: `${base}?s=settings`, icone: 'settings', texto: 'Definições' },
      ], recrutamento).map(({ to, ...resto }) => ({ para: to, ...resto }));

  return (
    <span className="dz-menu-ancora" ref={ancora}>
      <button
        ref={botao}
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-label="Menu de perfil"
        style={{
          display: 'inline-flex', padding: 0, border: 'none',
          background: 'transparent', cursor: 'pointer', borderRadius: '50%',
        }}
      >
        <span
          className="avatar avatar-sm"
          style={{ borderRadius: '50%', boxShadow: 'none' }}
          aria-hidden="true"
        >
          {inicial}
        </span>
      </button>

      {aberto && (
        <div className="dz-menu" role="menu">
          <div className="dz-menu-cabeca">
            <strong>{user?.full_name || 'Utilizador'}</strong>
            <span>{ROLE_LABELS[user?.role] || 'Conta'}</span>
          </div>

          {itens.map(({ para, icone, texto }) => (
            <Link
              key={para}
              to={para}
              role="menuitem"
              className="dz-menu-item"
              onClick={() => setAberto(false)}
            >
              <Icon name={icone} size={18} />
              {texto}
            </Link>
          ))}

          <button
            type="button"
            role="menuitem"
            className="dz-menu-item dz-menu-item--sair"
            onClick={sair}
          >
            <Icon name="logout" size={18} />
            Terminar sessão
          </button>
        </div>
      )}
    </span>
  );
};

export default UserMenu;
